import { FC, useCallback, useMemo, useState } from "react";
import { useHalls } from "api/hooks";
import { addHours, format, parse } from "date-fns";
import { matchIsValidTel } from "mui-tel-input";
import ReactGA from "react-ga4";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Checkbox, NativeSelect, PhoneInput, TextField, Typography } from "theme";
import { useError } from "utils/hooks";

import { zodResolver } from "@hookform/resolvers/zod";
import { Box, FormControl, Grid2, InputLabel, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";

import { useCreateBooking } from "../../../api/hooks/useCreateBooking/useCreateBooking.ts";
import { KitConfig } from "../../../api/hooks/useEquipmentKits/type.ts";
import useEquipmentKits from "../../../api/hooks/useEquipmentKits/useEquipmentKits.ts";
import { usePromocode } from "../../../api/hooks/usePromocode/usePromocode.ts";
import { PrepaymentOptions } from "../../../utils/constants/prepayment-options.ts";
import { REMINDER_OPTIONS } from "../../../utils/constants/reminder-options.ts";
import useSelectedEquipment from "../../../utils/hooks/useSelectedEquipment.ts";
import ControlledTextField from "../../ControlledInputs/ControlledTextField.tsx";
import SubmitButton from "../Submit/SubmitButton.tsx";

import ControlledPrepaymentGroup from "./components/ControlledPrepaymentGroup.tsx";
import { PersonalInfoFormScheme, personalInfoScheme } from "./validation.ts";

type PersonalInfoFormProps = {
  defaultValues: PersonalInfoFormScheme;
};

const Label = styled(InputLabel)(({ theme }) => ({
  ...theme.typography.body1,
  color: theme.palette.text.primary,
  textTransform: "uppercase",
  userSelect: "none",
  marginBottom: theme.spacing(1.25),
}));

const LabelAsterisk = styled("span")(({ theme }) => ({
  marginLeft: theme.spacing(0.5),
  color: theme.palette.error.main,

  "&:before": { content: "'*'" },
}));

const StyledButton = styled("button")(({ theme }) => ({
  background: "none",
  border: "none",
  cursor: "pointer",
  borderRadius: "5px",
  transition: "all 0.3s",
  fontWeight: 600,
  padding: theme.spacing(0.5, 1),

  "&:hover:not(:disabled)": {
    color: "#4654c9",
  },
}));

const PersonalInfoForm: FC<PersonalInfoFormProps> = ({ defaultValues }) => {
  const theme = useTheme();
  const { halls } = useHalls();
  const [searchParams] = useSearchParams();
  const { selectedEquipment } = useSelectedEquipment();
  const { kits } = useEquipmentKits();

  const [promoCodeId, setPromoCodeId] = useState("");
  const [promoCode, setPromoCode] = useState<{
    promoCodeId: string;
    promoCodeAmount: number;
    validTo: string;
  } | null>(null);
  const validatePromoCodeProvider = usePromocode();

  const createBookingProvider = useCreateBooking();

  const selectedTime = useMemo(() => {
    const selectedDate = searchParams.get("d");
    const selectedTime = searchParams.get("t");
    const selectedRange = searchParams.get("r");

    const startDate = parse(`${selectedDate} ${selectedTime}`, "yyyy-MM-dd HH:mm", new Date());
    const endDate = addHours(startDate, Number(selectedRange));

    return {
      startDate,
      endDate,
    };
  }, [searchParams]);

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PersonalInfoFormScheme>({
    reValidateMode: "onSubmit",
    resolver: zodResolver(personalInfoScheme),
    defaultValues,
  });

  // TODO: add error handling
  useError(errors);

  const onSubmit: SubmitHandler<PersonalInfoFormScheme> = useCallback(
    async (data) => {
      ReactGA.event({
        category: "booking_creation",
        action: "creating_completed",
        label: `${searchParams.toString()}&prepaymentOption=${data.bookingPrepaymentValue}&defaultEquipment=${data.defaultEquipment}&reminder=${data.reminder}`,
      });
      localStorage.setItem("personalData", JSON.stringify(data));
      createBookingProvider
        .mutateAsync({
          hallId: Number(searchParams.get("h")),
          startDate: format(new Date(selectedTime.startDate), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
          endDate: format(new Date(selectedTime.endDate), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
          equipments: selectedEquipment.map((el) => ({
            id: el.id,
            startDate: format(new Date(el.startDate), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
            endDate: format(new Date(el.endDate), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
            count: el.count,
          })),
          customerName: data.name,
          customerPhone: data.phone,
          customerEmail: data.email,
          customerComment: data.comment,
          notifyBySmsHours: data.reminder,
          redirectUrl: `${window.location.origin}/booking/{bookingId}`,
          bookingPrepaymentValue: data.bookingPrepaymentValue,
          defaultEquipment: data.defaultEquipment,
          ...(promoCode && { promocodeId: promoCode.promoCodeId }),
        })
        .then((r) => {
          if (r?.paymentLink) {
            localStorage.removeItem("equipment");
            const link = document.createElement("a");
            link.href = r.paymentLink;
            link.target = "_self";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        })
        .catch((e) => {
          console.error(e);
        });
    },
    [
      createBookingProvider,
      promoCode,
      searchParams,
      selectedEquipment,
      selectedTime.endDate,
      selectedTime.startDate,
    ],
  );

  const calculatedPrice = useMemo(() => {
    const selectedHall = halls?.find((h) => String(h.id) === searchParams.get("h"));

    const selectedHour = searchParams.get("t");

    const isNightHour = selectedHour ? selectedHour >= "22:30" || selectedHour < "08:00" : false;

    const hallPrice =
      Number(isNightHour ? selectedHall?.nightPricePerHour : selectedHall?.pricePerHour) *
      Number(searchParams.get("r"));

    const equipmentPrice = selectedEquipment.reduce(
      (accumulator, currentValue) =>
        accumulator +
        currentValue.pricePerUnit *
          currentValue.count *
          (currentValue.isFixedPrice ? 1 : currentValue.range > 3 ? 3 : currentValue.range),
      0,
    );

    return isNaN(hallPrice)
      ? 0
      : promoCode
        ? hallPrice + equipmentPrice - promoCode.promoCodeAmount
        : hallPrice + equipmentPrice;
  }, [halls, promoCode, searchParams, selectedEquipment]);

  const validatePromoCode = useCallback(() => {
    validatePromoCodeProvider.mutateAsync(promoCodeId).then((response) => {
      ReactGA.event({
        category: "booking_creation",
        action: "promo_code_applied",
        label: response?.promoCodeId,
      });
      setPromoCode(response);
      toast.success("Промокод успішно застосовано");
    });
  }, [validatePromoCodeProvider, promoCodeId]);

  return (
    <Box
      component="form"
      autoComplete="off"
      id="personal-info-form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      mb={theme.spacing(16)}
    >
      <Typography variant="h2" textTransform="uppercase" weight="semiBold">
        Ваші дані
      </Typography>

      <Grid2 mt={2.5} container flexDirection="column" rowGap={2.5}>
        <Box>
          <ControlledTextField
            control={control}
            name="name"
            placeholder="Введіть ваше ім'я"
            label="Ім'я"
            required
            type="text"
          />
        </Box>
        <Box>
          <Controller
            name="phone"
            control={control}
            rules={{
              validate: (value) => matchIsValidTel(value),
            }}
            render={({ field: { ref: fieldRef, value, ...fieldProps }, fieldState }) => (
              <>
                <Label shrink={false} htmlFor="phone">
                  <Grid2 container display="inline-flex" component="span">
                    Телефон
                    <LabelAsterisk />
                  </Grid2>
                </Label>
                <PhoneInput
                  fullWidth
                  {...fieldProps}
                  value={value ?? ""}
                  inputRef={fieldRef}
                  defaultCountry="UA"
                  placeholder="(__) ___ __ __"
                  onlyCountries={["UA"]}
                  helperText={fieldState.invalid ? "Невалідний номер" : ""}
                  error={fieldState.invalid}
                  forceCallingCode
                  disableDropdown
                  getFlagElement={() => null}
                />
              </>
            )}
          />
        </Box>
        <Box>
          <ControlledTextField
            control={control}
            name="email"
            placeholder="Введіть ваш Емейл"
            label="Email"
            type="email"
            autoComplete="off"
          />
        </Box>
        <Box>
          <ControlledTextField
            control={control}
            name="comment"
            placeholder="Введіть ваш коментар"
            label="Коментар"
            type="text"
            autoComplete="off"
            multiline
          />
        </Box>
        <Box>
          <Label shrink={false} htmlFor="defaultEquipment">
            <Grid2 container display="inline-flex" component="span">
              Безкоштовний набір світла:
            </Grid2>
          </Label>
          <FormControl fullWidth>
            <NativeSelect
              variant="standard"
              value={String(watch("defaultEquipment"))}
              onChange={(e) => setValue("defaultEquipment", e.target.value)}
              inputProps={{
                name: "defaultEquipment",
                id: "defaultEquipment",
              }}
            >
              {kits?.map((el) => (
                <option key={el} value={el}>
                  {KitConfig[el]}
                </option>
              ))}
            </NativeSelect>
          </FormControl>
        </Box>
        <Box>
          <Label shrink={false} htmlFor="reminder">
            <Grid2 container display="inline-flex" component="span">
              Нагадування:
            </Grid2>
          </Label>
          <FormControl fullWidth>
            <NativeSelect
              variant="standard"
              value={String(watch("reminder"))}
              onChange={(e) => setValue("reminder", Number(e.target.value))}
              defaultValue={30}
              inputProps={{
                name: "reminder",
                id: "reminder",
              }}
            >
              {REMINDER_OPTIONS.map((el) => (
                <option key={el.value} value={String(el.value)}>
                  {el.label}
                </option>
              ))}
            </NativeSelect>
          </FormControl>
        </Box>
        <Box>
          <ControlledPrepaymentGroup
            name="bookingPrepaymentValue"
            options={[
              {
                label: "передоплата 50%",
                value: PrepaymentOptions.HALF,
              },
              {
                label: "передоплата 100%",
                value: PrepaymentOptions.FULL,
              },
            ]}
            control={control}
          />
        </Box>
        <Box>
          <TextField
            InputProps={{
              endAdornment: (
                <StyledButton
                  type="button"
                  onClick={validatePromoCode}
                  disabled={!promoCodeId || validatePromoCodeProvider.isPending}
                >
                  Застосувати
                </StyledButton>
              ),
            }}
            type="text"
            name="discount"
            label="Промокод"
            placeholder="Маєте промокод?"
            value={promoCodeId}
            onChange={(e) => setPromoCodeId(e.target.value)}
          />
        </Box>
        <Box>
          <Grid2 container flexDirection="row" flexWrap="nowrap" columnGap={1.5}>
            <div>
              <Checkbox
                value={watch("termsAccepted")}
                onChange={(e) =>
                  e.target.checked
                    ? setValue("termsAccepted", true)
                    : setValue("termsAccepted", false)
                }
              />
            </div>
            <div>
              <Typography variant="body1">
                Я даю згоду на обробку моїх персональних даних і підтверджую, що я прочитав і
                прийняв{" "}
                <a
                  style={{
                    color: theme.palette.text.placeholder,
                    textDecoration: "underline",
                  }}
                  target="_blank"
                  href="https://www.panorama-studio.com.ua/rules"
                >
                  Правила студії
                </a>
              </Typography>
              {errors.termsAccepted?.message && (
                <p
                  style={{
                    color: "#F34141",
                    fontSize: "0.75rem",
                    padding: 0,
                    margin: 0,
                  }}
                >
                  {errors.termsAccepted?.message}
                </p>
              )}
            </div>
          </Grid2>
        </Box>
      </Grid2>
      <SubmitButton
        loading={createBookingProvider.isPending}
        form="personal-info-form"
        text={`Оплатити ${watch("bookingPrepaymentValue") === PrepaymentOptions.FULL ? calculatedPrice : calculatedPrice / 2}₴`}
      >
        <Grid2 mb={1} container alignItems="center" justifyContent="space-between">
          <Typography fontSize={18} variant="body1" textTransform="uppercase" weight="bold">
            Загальна сума: {calculatedPrice} ₴
          </Typography>
        </Grid2>
      </SubmitButton>
    </Box>
  );
};

export default PersonalInfoForm;
